import React, { createContext, useCallback, useContext, useState } from 'react';

export type PaymentSelectionState = {
  expanded: boolean;
  tabValue: TabValue;
  creditCardError: string;
  paymentCardType: string;
  manuallyExpanded: boolean;
  setExpanded: (value: boolean, source?: 'manual') => void;
  setCreditCardError: (error: string) => void;
  setPaymentCardType: (type: string) => void;
  setTabValue: (value: TabValue) => void;
};

export enum TabValue {
  none = '',
  offline = 'Offline',
  online = 'Online',
}

const PaymentSelectionContext = createContext<PaymentSelectionState | undefined>(undefined);

export const usePaymentSelectionContext = (): PaymentSelectionState => {
  const context = useContext(PaymentSelectionContext);
  if (!context) {
    throw new Error('usePaymentSelection must be used within a PaymentSelectionProvider');
  }
  return context;
};

type PaymentSelectionProviderProps = {
  children: React.ReactNode;
};

export const PaymentSelectionProvider: React.FC<PaymentSelectionProviderProps> = ({ children }) => {
  const [expanded, setExpandedState] = useState(true);
  const [manuallyExpanded, setManuallyExpanded] = useState(false);
  const [tabValue, setTabValue] = useState<TabValue>(TabValue.online);
  const [creditCardError, setCreditCardError] = useState('');
  const [paymentCardType, setPaymentCardType] = useState<string>('');

  const setExpanded = useCallback((value: boolean, source?: 'manual'): void => {
    setExpandedState(value);
    if (source === 'manual') {
      setManuallyExpanded(true);
    } else if (!value) {
      setManuallyExpanded(false);
    }
  }, []);

  const value = {
    tabValue,
    expanded,
    creditCardError,
    paymentCardType,
    manuallyExpanded,
    setExpanded,
    setCreditCardError,
    setPaymentCardType,
    setTabValue,
  };

  return <PaymentSelectionContext.Provider value={value}>{children}</PaymentSelectionContext.Provider>;
};
